import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useQuery } from '../useQuery';
import { getUser, getUsers } from '@dispatch/shared/services';
import { InteractionStatus } from '@azure/msal-browser';

/**
 * Custom hook to fetch authenticated user data.
 *
 * This hook is specifically designed for the web version of Dispatch.
 * It uses the `useQuery` hook from `react-query` to fetch user data based on the provided user ID.
 * The query is enabled only if the user is authenticated, the access token is set, and there is no ongoing authentication interaction.
 *
 * @param {string} userId - The ID of the user to fetch.
 * @param {boolean} [isAccessTokenSet] - Optional flag to indicate if the access token is set.
 * @returns {object} The result of the `useQuery` hook, including the user data and query status.
 */
export const useGetUserAuthenticated = (
  userId: string,
  isAccessTokenSet?: boolean
) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  return useQuery(
    ['user', userId],
    () => getUsers({ pageSize: 1, externalId: userId }),
    {
      enabled:
        isAuthenticated &&
        inProgress === InteractionStatus.None &&
        isAccessTokenSet,
    }
  );
};

export const useGetUser = (userId: string) => {
  return useQuery(['userInfo', userId], () => getUser(userId));
};

/**
 * Get's user by their external ID. Useful for discovering current user.
 * @param externalId user's oid/sub from the JWT
 * @returns
 */
export const useGetUserByExternalId = (
  externalId: string,
  isAccessTokenSet: boolean
) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  return useQuery(
    ['user-external', externalId],
    () =>
      getUsers().then((x) => x?.data?.find((x) => x.externalId === externalId)),
    {
      enabled:
        isAuthenticated &&
        inProgress === InteractionStatus.None &&
        isAccessTokenSet,
    }
  );
};

export const useGetUserByExternalId_Mobile = (externalId: string) => {
  return useQuery(['user-external', externalId], () =>
    getUsers().then((x) => x?.data?.find((x) => x.externalId === externalId))
  );
};
